import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot,
  faTachometerAlt,
  faNetworkWired,
  faCogs,
  faSignOutAlt,
  faUser,
  faUsers,
  faTools,
  faComments,
  faPlay,
  faSitemap,
  faTasks,
  faChartBar,
  faClock,
  faUniversity
} from '@fortawesome/free-solid-svg-icons';

import { useAuth } from '../../context/AuthContext';
import GruveLogo from '../UI/GruveLogo';

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();




  // Build static navigation items based on user role
  const getStaticNavigationItems = () => {
    const items = [
      {
        path: '/dashboard',
        label: 'Dashboard',
        icon: faTachometerAlt,
        color: 'from-blue-500 to-cyan-500',
        roles: ['admin', 'customer', 'viewer'] // All roles
      }
    ];

    // Admin-only items
    if (user?.role === 'admin') {
      items.push({
        path: '/users',
        label: 'User Management',
        icon: faUsers,
        color: 'from-orange-500 to-red-500',
        roles: ['admin']
      });
      items.push({
        path: '/tools',
        label: 'Tools Management',
        icon: faTools,
        color: 'from-purple-500 to-indigo-500',
        roles: ['admin']
      });
    }

    // Connectors - visible for both admin and customer
    if (user?.role === 'admin' || user?.role === 'customer') {
      items.push({
        path: '/connectors',
        label: 'Connectors',
        icon: faNetworkWired,
        color: 'from-green-500 to-teal-500',
        roles: ['admin', 'customer']
      });
    }

    // Customer and Admin can see Agents
    if (user?.role === 'admin' || user?.role === 'customer') {
      items.push({
        path: '/agents',
        label: 'Agents',
        icon: faRobot,
        color: 'from-purple-500 to-pink-500',
        roles: ['admin', 'customer']
      });
    }

    // Admin-only workflow and management items
    if (user?.role === 'admin') {
      items.push({
        path: '/workflows',
        label: 'Workflows',
        icon: faSitemap,
        color: 'from-indigo-500 to-blue-500',
        roles: ['admin']
      });

      items.push({
        path: '/reports',
        label: 'Reports',
        icon: faChartBar,
        color: 'from-amber-500 to-orange-500',
        roles: ['admin']
      });

      items.push({
        path: '/cron-jobs',
        label: 'Cron Jobs',
        icon: faClock,
        color: 'from-teal-500 to-emerald-500',
        roles: ['admin']
      });
    }

    // Task Management - visible for admin and customer
    if (user?.role === 'admin' || user?.role === 'customer') {
      items.push({
        path: '/tasks',
        label: 'Task Management',
        icon: faTasks,
        color: 'from-pink-500 to-red-500',
        roles: ['admin', 'customer']
      });
    }
    
    // Chat Threads - Admin only
    if (user?.role === 'admin') {
      items.push({
        path: '/threads',
        label: 'Chat Threads',
        icon: faComments,
        color: 'from-blue-500 to-indigo-500',
        roles: ['admin']
      });

      // Bank Agent Chat History - Admin only
      items.push({
        path: '/bank-chat-history',
        label: 'Bank Agent History',
        icon: faUniversity,
        color: 'from-emerald-500 to-teal-500',
        roles: ['admin']
      });
    }

    items.push({
      path: '/settings',
      label: 'Settings',
      icon: faCogs,
      color: 'from-gray-500 to-gray-600',
      roles: ['admin', 'customer', 'viewer']
    });

    return items;
  };

  const staticNavigationItems = getStaticNavigationItems();


  const handleLogout = async () => {
    await logout();
    navigate('/login');
    if (onClose) onClose();
  };

  const sidebarVariants = {
    open: { x: 0 },
    closed: { x: '-100%' }
  };

  const itemVariants = {
    open: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.3
      }
    }),
    closed: {
      opacity: 0,
      x: -20
    }
  };

  const formatUserRole = (role) => {
    switch (role) {
      case 'admin':
        return 'Administrator';
      case 'customer':
        return 'Customer';
      case 'viewer':
        return 'Viewer';
      default:
        return 'User';
    }
  };

  const getUserInitials = (user) => {
    if (user?.name) {
      return user.name.substring(0, 2).toUpperCase();
    }
    if (user?.username) {
      return user.username.substring(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <motion.aside
        variants={sidebarVariants}
        animate={isOpen ? 'open' : 'closed'}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className={`
          fixed top-0 left-0 h-screen w-80 bg-white dark:bg-gray-900 shadow-2xl z-50
          lg:shadow-none lg:translate-x-0
          border-r border-gray-200 dark:border-gray-700
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center mb-6">
              <GruveLogo size="w-32 h-auto" className="" />
            </div>

            {/* User Info */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold shadow-lg">
                  {getUserInitials(user)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                    {user?.name || user?.username || 'User'}
                  </h3>
                  <p className="text-sm text-indigo-600 dark:text-indigo-400 flex items-center">
                    <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2 shadow-sm"></span>
                    {formatUserRole(user?.role)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
            {/* Static Navigation Items */}
            {staticNavigationItems.map((item, index) => {
              const isActive = location.pathname === item.path;
              return (
                <motion.div
                  key={item.path}
                  variants={itemVariants}
                  custom={index}
                  initial="closed"
                  animate="open"
                >
                  <NavLink
                    to={item.path}
                    onClick={() => onClose && onClose()}
                    className={({ isActive }) => `
                      group flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200
                      ${isActive 
                        ? `bg-gradient-to-r ${item.color} text-white shadow-lg transform scale-105` 
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                      }
                    `}
                  >
                    <div className={`
                      w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200
                      ${isActive 
                        ? 'bg-white/20 text-white' 
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 group-hover:bg-gray-200 dark:group-hover:bg-gray-600'
                      }
                    `}>
                      <FontAwesomeIcon icon={item.icon} className="text-sm" />
                    </div>
                    <span className="font-medium">{item.label}</span>
                    {isActive && (
                      <div className="ml-auto w-2 h-2 bg-white rounded-full shadow-sm"></div>
                    )}
                  </NavLink>
                </motion.div>
              );
            })}

          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-all duration-200 group"
            >
              <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center group-hover:bg-red-200 dark:group-hover:bg-red-900/50 transition-all duration-200">
                <FontAwesomeIcon icon={faSignOutAlt} className="text-sm" />
              </div>
              <span className="font-medium">Sign Out</span>
            </button>

            {/* Version */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Platform v2.0
              </p>
            </div>
          </div>
        </div>
      </motion.aside>
    </>
  );
};

export default Sidebar;